import * as React from 'react';
import { Link } from 'react-router-dom';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { getRowFromInvite, getRowFromUser } from '../../services/team-table-data-convertor';
import { InviteData, UserData } from '../../types/teamMembers';
import { getFullName, getInitials } from '../services/row-helper';
import { UserNameCell } from '../TeamMembersList/TeamMembersListComponents';
import * as css from './TeammatesPopover.scss';
import { useCallback } from 'react';

import { Avatar, Box, Button, Popover, Heading } from 'wix-style-react';

interface IProps extends InjectedTranslateProps {
  users: UserData[];
  invites: InviteData[];
  seeAllPath: string;
  role: string;
  onClick?: any;
}

type ITeammatesPopoverComposition = React.FC<IProps> & {
  Users: React.FC<{ users: UserData[] }>
  Invites: React.FC<{ invites: InviteData[] }>
};

const TeammatesPopover: ITeammatesPopoverComposition = ({ t, users, invites, onClick, seeAllPath, role, children }) => {
  const [shown, setShown] = React.useState<boolean>();
  const onClickAction = useCallback(() => onClick(role), [role]);

  return (
    <Popover placement="auto" showArrow animate shown={shown} onMouseEnter={() => setShown(true)} onMouseLeave={() => setShown(false)} dataHook="teammate-popover">
      <Popover.Element>
        {children}
      </Popover.Element>
      <Popover.Content>
        <Box direction="vertical" maxHeight="310px">
          <Box direction="vertical" className={css.content}  overflow="auto">
            <TeammatesPopover.Users users={users} />
            {/* NOTE: Invites are currently out of the design, so it's commented out until it will be final */}
            {invites && <Heading appearance="H5" className={css.pendingHeader}>{t('list.pending')}</Heading>}
            <TeammatesPopover.Invites invites={invites} />
          </Box>
          <Box className={css.seeAll}>
            <Button
              fullWidth
              as={Link}
              to={seeAllPath}
              onClick={onClickAction}
              dataHook="teammate-popover-see-all"
            >{t('teammatesPopover.buttonTitle')}</Button>
          </Box>
        </Box>
      </Popover.Content>
    </Popover>
  );
};

const Users = ({ users }) => {
  if (!users) { return null; }
  return users.map((user: UserData) => {
    const rowData = getRowFromUser(user);
    return (
      <Box direction="horizontal" className={css.item} key={user.id} verticalAlign="middle">
        <Box>
          <Avatar
            dataHook="user-image"
            ariaLabel={`Avatar for ${'placeholder name'}`}
            name={rowData.email}
            text={getInitials(rowData)}
            imgProps={{ src: user.profileImage }}
            size="size36"
          />
        </Box>
        <Box className={css.name}>
          <UserNameCell title={getFullName(rowData)} subTitle={rowData.email}/>
        </Box>
      </Box>
    );
  });
};

const Invites = ({ invites }) => {
  if (!invites) { return null; }
  return invites.map((invite: InviteData) => {
    const rowData = getRowFromInvite(invite);
    const email = rowData.email;
    const name = email.substring(0, email.lastIndexOf('@'));

    return (
      <Box direction="horizontal" className={css.item} key={invite.id} verticalAlign="middle">
        <Box>
          <Avatar
            dataHook="invite-image"
            ariaLabel={`Avatar for ${'placeholder name'}`}
            name={email}
            text={getInitials(rowData)}
            size="size36"
          />
        </Box>
        <Box className={css.name}>
          <UserNameCell title={name} subTitle={email}/>
        </Box>
      </Box>
    );
  });
};

TeammatesPopover.Users = Users;
TeammatesPopover.Invites = Invites;

export default translate()(TeammatesPopover);
